import {useZeroProps} from '@/shared/integrations/zero/use-zero-props'
import {queries} from '@cypro/engine/queries'
import {dropDatabase} from '@rocicorp/zero'
import {useZero, ZeroProvider as ZProvider} from '@rocicorp/zero/react'
import {PropsWithChildren, useEffect} from 'react'

export function ZeroProvider({children}: PropsWithChildren) {
  const props = useZeroProps()

  return (
    <ZProvider {...props}>
      <PreloadLocations />
      <DropOutdatedDatabases />
      {children}
    </ZProvider>
  )
}

// Mutators that create location-scoped records (pharmacies, sections) look up
// the location client-side, so keep it synced for the whole session.
function PreloadLocations() {
  const z = useZero()

  useEffect(() => {
    const {cleanup} = z.preload(queries.locations.listLocations())
    return cleanup
  }, [z])

  return null
}

// Each schema version syncs into its own IndexedDB, and Zero only collects the old ones after weeks. Drop them now,
// so columns removed from the schema (password hashes and tokens, CY-374) leave the browser on the next start.
// Zero has no mutation recovery, so an outdated database holds nothing that could still be pushed.
function DropOutdatedDatabases() {
  const z = useZero()

  useEffect(() => {
    if (!('databases' in indexedDB)) return
    indexedDB
      .databases()
      .then(databases =>
        Promise.all(
          databases
            .map(database => database.name)
            .filter(name => name?.startsWith('rep:') && !name.endsWith(`:${z.schemaVersion}`))
            .map(name => dropDatabase(name!))
        )
      )
      .catch((error: unknown) => console.warn('[Zero] Failed to drop outdated databases:', error))
  }, [z])

  return null
}
